import {
  inboundShipmentIdFormat,
  locationIdFormat,
  productFamilyIdFormat,
  productIdFormat,
  purchaseOrderFileIdFormat,
  purchaseOrderIdFormat,
  purchaseOrderProductIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import { PurchaseOrderStatus } from "@redotech/redo-prisma-client/enums";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// Product info in purchase order
const PurchaseOrderProductInfoSchema_ = z.object({
  productFamilyId: productFamilyIdFormat.schema,
});

export const PurchaseOrderProductInfoSchema: z.ZodType<PurchaseOrderProductInfo> =
  PurchaseOrderProductInfoSchema_;
export interface PurchaseOrderProductInfo
  extends z.infer<typeof PurchaseOrderProductInfoSchema_> {}

const PurchaseOrderProductSchema_ = z.object({
  id: purchaseOrderProductIdFormat.schema,
  quantity: z.number().int(),
  receivedQuantity: z.number().int(),
  damagedQuantity: z.number().int(),
  unitCost: z.number(),
  costCurrency: z.string(),
  totalTax: z.number(),
  taxCurrency: z.string(),

  productId: productIdFormat.schema,
  title: z.string(),
  sku: z.string().nullish(),
  image: z.string().nullish(),
  vendor: z.string(),

  productFamilyId: productFamilyIdFormat.schema.nullish(),
  productFamilyTitle: z.string().nullish(),
  productFamilyImage: z.string().nullish(),

  // Shopify cost (from product.cost)
  shopifyCost: z.number().nullish(),
  shopifyCostCurrency: z.string().nullish(),
});

export const PurchaseOrderProductSchema: z.ZodType<PurchaseOrderProduct> =
  PurchaseOrderProductSchema_;
export interface PurchaseOrderProduct
  extends z.infer<typeof PurchaseOrderProductSchema_> {}

// Shipment product assignment
const ShipmentProductSchema_ = z.object({
  productId: productIdFormat.schema,
  quantity: z.number().int(),
});

export const ShipmentProductSchema: z.ZodType<ShipmentProduct> =
  ShipmentProductSchema_;
export interface ShipmentProduct
  extends z.infer<typeof ShipmentProductSchema_> {}

// Inbound shipment details
const InboundShipmentSchema_ = z.object({
  id: inboundShipmentIdFormat.schema,
  location: z
    .object({ id: locationIdFormat.schema, name: z.string() })
    .nullish(),
  carrier: z.string().nullish(),
  estimatedArrivalDate: zExt.plainDate().nullish(),
  shipmentId: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  freightCost: z.number(),
  freightCurrency: z.string(),
  dutyCost: z.number(),
  dutyCurrency: z.string(),
  products: z.array(ShipmentProductSchema),
});

export const InboundShipmentSchema: z.ZodType<InboundShipment> =
  InboundShipmentSchema_;
export interface InboundShipment
  extends z.infer<typeof InboundShipmentSchema_> {}

// Purchase order file
export const PurchaseOrderFileSchema_ = z.object({
  id: purchaseOrderFileIdFormat.schema,
  title: z.string(),
  url: z.string(),
  contentType: z.string(),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  createdAt: zExt.instant(),
});

export const PurchaseOrderFileSchema: z.ZodType<PurchaseOrderFile> =
  PurchaseOrderFileSchema_;
export interface PurchaseOrderFile
  extends z.infer<typeof PurchaseOrderFileSchema_> {}

// Complete purchase order details
const PurchaseOrderDetailsSchema_ = z.object({
  id: purchaseOrderIdFormat.schema,
  poNumber: z.string(),
  reference: z.string().nullish(),
  supplier: z
    .object({
      id: supplierIdFormat.schema,
      name: z.string(),
      currency: z.string().nullish(),
      leadTimeDays: z.number().nullish(),
    })
    .nullish(),
  location: z
    .object({ id: locationIdFormat.schema, name: z.string() })
    .nullish(),
  currency: z.string(),
  status: createEnum(PurchaseOrderStatus),
  tags: z.array(z.string()),
  trackingUrl: z.string().nullish(),
  note: z.string().nullish(),
  noteToSupplier: z.string().nullish(),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  products: z.array(PurchaseOrderProductSchema),
  shipments: z.array(InboundShipmentSchema),
  files: z.array(PurchaseOrderFileSchema),
  createdAt: zExt.instant(),
  updatedAt: zExt.instant(),
  closedAt: zExt.instant().nullable(),
});

export const PurchaseOrderDetailsSchema: z.ZodType<PurchaseOrderDetails> =
  PurchaseOrderDetailsSchema_;
export interface PurchaseOrderDetails
  extends z.infer<typeof PurchaseOrderDetailsSchema_> {}

export const getPurchaseOrder = {
  name: "getPurchaseOrder" as const,
  input: z.object({ purchaseOrderId: purchaseOrderIdFormat.schema }),
  output: z.object({ purchaseOrder: PurchaseOrderDetailsSchema }),
};

export { PurchaseOrderStatus } from "@redotech/redo-prisma-client/enums";
